import React, { useState } from 'react';
import {
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  Alert,
  Amount,
  ArrowLeftIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  MoreVerticalIcon,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  TableToolbar,
  TableToolbarActions,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useToast,
  TrashIcon,
  IconButton,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import useOrgBalance from 'components/Fnf/ReleaseNetpay/useOrgBalance';
import { useLoanData } from '../useLoanData';
import { LOAN_STATUS } from '../constants';
import { formatDate } from '../utils';
import { LoanDataArraySchemaContract } from 'schemas/LoanDataArraySchema';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';

type Loan = LoanDataArraySchemaContract['data'][number] & {
  created_at?: string | null;
  loan_category_name?: string | null;
};

type PendingDisbursementsViewProps = {
  onBack: () => void;
};

export const PendingDisbursementsView = ({ onBack }: PendingDisbursementsViewProps) => {
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDisburseOpen, setIsDisburseOpen] = useState(false);
  const [isMarkPaidOpen, setIsMarkPaidOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const isRXIntegrated = useSelector(loggedInUserSelectors.isOrgRXIntegrated);
  const currentOrg = useSelector(loggedInUserSelectors.currentOrganization);
  const isRxCurrentAccountMerchant = currentOrg?.isRxCurrentAccountMerchant;
  const { balance, isLoading: isBalanceLoading } = useOrgBalance();

  const debitFrom =
    isRXIntegrated && isRxCurrentAccountMerchant
      ? 'Current Account'
      : isRXIntegrated
      ? 'RazorpayX Wallet'
      : 'XPayroll Wallet';

  const { data, isFetching } = useLoanData(LOAN_STATUS.APPROVED);
  const loans = (data?.data ?? []) as Loan[];

  const selectedCount = selectedIds.size;
  const allChecked = loans.length > 0 && selectedCount === loans.length;

  const toggleAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(loans.map((l) => l.id)) : new Set());

  const toggleOne = (id: number, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });

  const selectedLoans = loans.filter((l) => selectedIds.has(l.id));
  const totalSelectedAmount = selectedLoans.reduce((sum, l) => sum + (l.principal ?? 0), 0);

  const disburseMutation = useMutation({
    mutationFn: () => api.loans.disburseLoans(),
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      setIsDisburseOpen(false);
      showToast({ content: 'Amount disbursed successfully!', color: 'positive', duration: 4000 });
      onBack();
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to initiate disbursement. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: () => api.loans.markLoansAsPaid(Array.from(selectedIds)),
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      setIsMarkPaidOpen(false);
      showToast({
        content: 'Loans marked as paid successfully.',
        color: 'positive',
        duration: 4000,
      });
      onBack();
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to mark loans as paid. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.loans.bulkDeleteLoans(Array.from(selectedIds));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      setIsDeleteOpen(false);
      setSelectedIds(new Set());
      showToast({
        content: selectedCount > 1 ? `${selectedCount} loans deleted.` : 'Loan deleted.',
        color: 'neutral',
        duration: 3000,
      });
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to delete loan(s). Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  return (
    <Box padding="spacing.8">
      <Box display="flex" alignItems="center" gap="spacing.3" marginBottom="spacing.6">
        <IconButton icon={ArrowLeftIcon} size="large" accessibilityLabel="Back" onClick={onBack} />
        <Heading size="large" weight="semibold">
          Pending Disbursements
        </Heading>
      </Box>

      <Table<Loan>
        data={{ nodes: loans }}
        isLoading={isFetching}
        gridTemplateColumns="40px 1.6fr 1.5fr 1.5fr 0.8fr 1fr 1fr"
        toolbar={
          <TableToolbar title={`Showing ${loans.length} Item(s)`}>
            <TableToolbarActions>
              <Box display="flex" gap="spacing.3" alignItems="center" flexShrink={0}>
                <Dropdown>
                  <DropdownButton
                    isDisabled={selectedCount === 0}
                    variant="tertiary"
                    icon={MoreVerticalIcon}
                  />
                  <DropdownOverlay>
                    <ActionList>
                      {[
                        <ActionListItem
                          key="bulk-delete"
                          title="Delete"
                          value="bulk-delete"
                          intent="negative"
                          isDisabled={selectedCount === 0}
                          leading={<ActionListItemIcon icon={TrashIcon} />}
                          onClick={() => setIsDeleteOpen(true)}
                        />,
                      ]}
                    </ActionList>
                  </DropdownOverlay>
                </Dropdown>
                <Button
                  variant="secondary"
                  isDisabled={selectedCount === 0}
                  onClick={() => setIsMarkPaidOpen(true)}>
                  Mark as Paid
                </Button>
                <Button isDisabled={selectedCount === 0} onClick={() => setIsDisburseOpen(true)}>
                  Disburse
                </Button>
              </Box>
            </TableToolbarActions>
          </TableToolbar>
        }>
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Checkbox
                    isChecked={allChecked}
                    isIndeterminate={selectedCount > 0 && !allChecked}
                    onChange={({ isChecked }) => toggleAll(isChecked)}>
                    {''}
                  </Checkbox>
                </TableHeaderCell>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>Employee</TableHeaderCell>
                <TableHeaderCell>Reason</TableHeaderCell>
                <TableHeaderCell>Tenure</TableHeaderCell>
                <TableHeaderCell textAlign="right">Amount</TableHeaderCell>
                <TableHeaderCell textAlign="right">EMI</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {tableData.map((loan) => {
                const isChecked = selectedIds.has(loan.id);
                return (
                  <TableRow key={loan.id} item={loan}>
                    <TableCell>
                      <Checkbox
                        isChecked={isChecked}
                        onChange={({ isChecked: c }) => toggleOne(loan.id, c)}>
                        {''}
                      </Checkbox>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Text size="small" weight="semibold">
                          #{loan.id}
                        </Text>
                        <Text size="xsmall" color="surface.text.gray.muted">
                          {formatDate(loan.created_at)}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Text size="small">{loan.name ?? '—'}</Text>
                        {loan.employee_id && (
                          <Text size="xsmall" color="surface.text.gray.muted">
                            {loan.employee_id}
                          </Text>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text size="small" truncateAfterLines={1}>
                        {loan.loan_category_name ?? '—'}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text size="small">{loan.tenure ? `${loan.tenure} months` : '—'}</Text>
                    </TableCell>
                    <TableCell textAlign="right">
                      <Amount value={loan.principal} size="small" />
                    </TableCell>
                    <TableCell textAlign="right">
                      <Amount value={loan.monthly_total_emi ?? 0} size="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>

      {!isFetching && loans.length === 0 && (
        <EmptyListView
          title="No pending disbursements"
          description={
            <Text color="surface.text.gray.muted" textAlign="center">
              No approved loans pending disbursement for your organization.
            </Text>
          }
        />
      )}

      <Modal isOpen={isDisburseOpen} onDismiss={() => setIsDisburseOpen(false)} size="small">
        <ModalHeader title="Disbursement" />
        <ModalBody>
          <Box display="flex" flexDirection="column" gap="spacing.5">
            <Box
              borderWidth="thin"
              borderRadius="medium"
              borderColor="surface.border.gray.subtle"
              padding="spacing.5">
              <Text size="small" weight="semibold" marginBottom="spacing.4">
                Disbursement Details
              </Text>
              <Divider marginBottom="spacing.4" />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="spacing.4">
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Total Selected
                  </Text>
                  <Text size="medium" weight="semibold">
                    {selectedCount}
                  </Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Total Amount
                  </Text>
                  <Amount value={totalSelectedAmount} size="medium" weight="semibold" />
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Debit from
                  </Text>
                  <Text size="small" weight="semibold">
                    {debitFrom}
                  </Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Wallet Balance
                  </Text>
                  {isBalanceLoading ? (
                    <Text size="small" weight="semibold">
                      —
                    </Text>
                  ) : (
                    <Amount value={balance ?? 0} size="small" weight="semibold" />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" width="100%">
            <Button variant="secondary" isFullWidth onClick={() => setIsDisburseOpen(false)}>
              Cancel
            </Button>
            <Button
              isFullWidth
              isLoading={disburseMutation.isLoading}
              onClick={() => disburseMutation.mutate()}>
              Disburse Now
            </Button>
          </Box>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isMarkPaidOpen} onDismiss={() => setIsMarkPaidOpen(false)} size="small">
        <ModalHeader title="Mark as Paid" />
        <ModalBody>
          <Box display="flex" flexDirection="column" gap="spacing.5">
            <Box
              borderWidth="thin"
              borderRadius="medium"
              borderColor="surface.border.gray.subtle"
              padding="spacing.5">
              <Text size="small" weight="semibold" marginBottom="spacing.4">
                Loan Details
              </Text>
              <Divider marginBottom="spacing.4" />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="spacing.4">
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Total Amount
                  </Text>
                  <Amount value={totalSelectedAmount} size="medium" weight="semibold" />
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Total Selected
                  </Text>
                  <Text size="medium" weight="semibold">
                    {selectedCount}
                  </Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    New Status
                  </Text>
                  <Badge color="positive" marginTop="spacing.1">
                    Active
                  </Badge>
                </Box>
              </Box>
            </Box>
            <Alert
              color="information"
              isDismissible={false}
              description="The selected loan(s) will be activated without disbursement, as the amount was disbursed outside payroll."
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" width="100%">
            <Button variant="secondary" isFullWidth onClick={() => setIsMarkPaidOpen(false)}>
              Cancel
            </Button>
            <Button
              isFullWidth
              isLoading={markPaidMutation.isLoading}
              onClick={() => markPaidMutation.mutate()}>
              Confirm
            </Button>
          </Box>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isDeleteOpen} onDismiss={() => setIsDeleteOpen(false)} size="small">
        <ModalHeader
          title="Delete Loan?"
          subtitle={`Permanently deleting ${selectedCount} Loan${selectedCount > 1 ? 's' : ''}`}
        />
        <ModalBody>
          <Text size="medium" color="surface.text.gray.subtle">
            This action is permanent and cannot be undone. The loan{selectedCount > 1 ? 's' : ''}{' '}
            will be permanently deleted and removed from the system.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" width="100%">
            <Button variant="secondary" isFullWidth onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              color="negative"
              isFullWidth
              isLoading={deleteMutation.isLoading}
              onClick={() => deleteMutation.mutate()}>
              Delete
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </Box>
  );
};
